import { useQuery } from '@apollo/client';
import Dropdown from 'components/form/Dropdown';
import Input from 'components/form/Input';
import { ToggleView } from 'components/general';
import type { ChannelIntegrationEditorProps } from 'components/integrations/ChannelIntegrationEditor/ChannelIntegrationEditorProps';
import React, { useMemo, useState } from 'react';
import rightArrow from '../../../../static/img/right-arrow.svg';
import styles from './FloorMapping.scss';
import type { IntegrationPropertyType } from 'application-types';
import { ListPropertyFloorsDocument } from 'graphql-query-contracts';
import { convertToUUID } from 'uuid-encoding';
import {
  EntityMapping,
  useEntityMappings,
} from '../TenantMapping/useEntityMappings';

type PropertyProps = {
  property: IntegrationPropertyType;
  value?: any;
  label?: string;
  onChange: (value: any) => void;
  channel: ChannelIntegrationEditorProps['channel'];
  settings: ChannelIntegrationEditorProps['channelIntegration']['settings'];
  metadataLoading?: boolean;
  floorKey: string;
  sourceDisplayName?: string;
  providerDisplayName?: string;
  providerFloorOptions?: FloorOption[];
  targetFloorTestIdPrefix?: string;
  targetFloorPlaceholder?: string;
};

type VtsFloor = {
  id: string;
  name?: string | null;
  index?: number | null;
};

type FloorOption = {
  label: string;
  value: string;
  isDisabled?: boolean;
};

type MappedFloor = {
  label: string;
  value: string;
  providerFloorValue: string;
};

type FloorMappingPayload = {
  vtsFloorId: string;
  prevIgsValue?: string;
  [key: string]: string | undefined;
};

type StoredFloorMapping = {
  vtsFloorId?: string;
  prevIgsValue?: string;
  [key: string]: string | undefined;
};

const INTEGRATED_ENTITY_TYPE_FLOOR = 'INTEGRATED_ENTITY_TYPE_FLOOR';

const getFloorLabel = (floor: VtsFloor) => {
  if (floor?.name && floor.name.trim().length > 0) {
    return floor.name;
  }

  return floor?.index != null ? `Floor ${floor.index}` : 'Unnamed floor';
};

const normalizeFloorId = (id?: string) => {
  if (!id) {
    return '';
  }

  try {
    return convertToUUID(id);
  } catch {
    return id;
  }
};

const getMappedFloorValueFromEntityMappings = (
  entityMappings: EntityMapping[],
  floorId: string
) => {
  const normalizedFloorId = normalizeFloorId(floorId);

  const mappedFloor = entityMappings.find(mapping => {
    const sourceEntityId = mapping?.vtsEntityId || mapping?.cesSei?.sourceEntityId;

    return normalizeFloorId(sourceEntityId) === normalizedFloorId;
  });

  return mappedFloor?.thirdPartyEntityId || '';
};

const getOriginalMappedFloorValue = ({
  floorId,
  floorKey,
  floorMappings,
  entityMappings,
}: {
  floorId: string;
  floorKey: string;
  floorMappings?: StoredFloorMapping[];
  entityMappings: EntityMapping[];
}) => {
  const savedFloorMapping = floorMappings?.find(
    mapping =>
      normalizeFloorId(mapping?.vtsFloorId) === normalizeFloorId(floorId)
  );

  if (savedFloorMapping?.prevIgsValue !== undefined) {
    return savedFloorMapping.prevIgsValue;
  }

  const savedMappedValue = savedFloorMapping?.[floorKey];

  if (savedMappedValue !== undefined) {
    return savedMappedValue;
  }

  return getMappedFloorValueFromEntityMappings(entityMappings, floorId);
};

export const FloorMapping = ({
  property,
  label,
  onChange,
  channel,
  settings,
  metadataLoading,
  floorKey,
  sourceDisplayName = 'Activate',
  providerDisplayName = 'Access control',
  providerFloorOptions,
  targetFloorTestIdPrefix = 'igs-floor',
  targetFloorPlaceholder = 'Not mapped',
}: PropertyProps) => {
  const { data: propertyFloorsData } = useQuery(ListPropertyFloorsDocument, {
    variables: { propertyId: convertToUUID(channel._id) },
  });
  const channelId = useMemo(() => convertToUUID(channel._id), [channel._id]);
  const { entityMappings } = useEntityMappings({
    channelId,
    integratedEntityType: INTEGRATED_ENTITY_TYPE_FLOOR,
    domain: 'DOMAIN_VISITORS',
  });

  const [localFloorOverrides, setLocalFloorOverrides] = useState<
    Record<string, string>
  >({});

  const acsFloorList = useMemo(() => {
    if (metadataLoading) {
      return [];
    }

    const floorOptionsCandidate = property[floorKey as keyof IntegrationPropertyType];
    const floorOptionsFromProperty = Array.isArray(floorOptionsCandidate)
      ? (floorOptionsCandidate as FloorOption[])
      : [];
    const hasProviderOptions =
      Array.isArray(providerFloorOptions) && providerFloorOptions.length > 0;

    return hasProviderOptions ? providerFloorOptions : floorOptionsFromProperty;
  }, [metadataLoading, property, floorKey, providerFloorOptions]);

  const vtsFloorList = useMemo(() => {
    if (metadataLoading) {
      return [];
    }

    const floors = (propertyFloorsData?.property?.floors || []).filter(
      Boolean
    ) as VtsFloor[];

    return floors
      .map(floor => {
        const hasLocalOverride = Object.prototype.hasOwnProperty.call(
          localFloorOverrides,
          floor.id
        );
        const localOverrideValue = localFloorOverrides[floor.id];
        const localFloorMapping = settings?.floorMappings?.find(
          (item: { vtsFloorId: string }) =>
            normalizeFloorId(item.vtsFloorId) === normalizeFloorId(floor.id)
        );
        const localMappedFloorId = localFloorMapping?.[floorKey] || '';
        const mappedFloorId = hasLocalOverride
          ? localOverrideValue
          : localMappedFloorId ||
            getMappedFloorValueFromEntityMappings(entityMappings, floor.id);

        return {
          label: getFloorLabel(floor),
          value: floor.id,
          providerFloorValue: mappedFloorId,
        };
      })
      .sort((a: MappedFloor, b: MappedFloor) => a.label.localeCompare(b.label));
  }, [
    metadataLoading,
    propertyFloorsData,
    settings?.floorMappings,
    floorKey,
    entityMappings,
    localFloorOverrides,
  ]);

  const handleChange = (
    nextProviderFloor: {
      label?: string;
      value: string;
      isDisabled?: boolean | undefined;
    },
    id: number
  ) => {
    if (vtsFloorList[id]?.providerFloorValue !== nextProviderFloor?.value) {
      const floorId = vtsFloorList[id]?.value;

      if (!floorId) {
        return;
      }

      const updatedFloorValue = nextProviderFloor?.value || '';

      setLocalFloorOverrides(previous => ({
        ...previous,
        [floorId]: updatedFloorValue,
      }));

      // Build full array so parent state retains all mappings; onChange replaces
      // the entire settings.floorMappings array via onUpdateSettings.
      // Add prevIgsValue only for changed mappings so sync sends only deltas (matches tenant mapping pattern).
      const floorMappingPayload: FloorMappingPayload[] = vtsFloorList
        .map((item, idx) => {
          const vtsId = item.value;
          const providerValue =
            idx === id ? updatedFloorValue : (item.providerFloorValue || '');
          const originalValue = getOriginalMappedFloorValue({
            floorId: vtsId,
            floorKey,
            floorMappings: settings?.floorMappings as StoredFloorMapping[] | undefined,
            entityMappings,
          });
          const hasChanged = originalValue !== providerValue;
          return {
            vtsFloorId: vtsId,
            [floorKey]: providerValue,
            ...(hasChanged && { prevIgsValue: originalValue }),
          };
        })
        .filter((mapping: FloorMappingPayload) => Boolean(mapping[floorKey]));

      onChange(floorMappingPayload);
    }
  };

  const renderCollapseView = () => {
    return (
      <div>
        <div className={styles.parentDescription}>
          Map this property&apos;s {sourceDisplayName} floors to floor records in{' '}
          {providerDisplayName}.
        </div>
        <div className={styles.count}>
          {vtsFloorList?.filter(item => item?.providerFloorValue)?.length || 0}{' '}
          Floors mapped
        </div>
      </div>
    );
  };

  return (
    <div className={styles.floorMapping}>
      <div className={styles.mappingBox}>
        <ToggleView
          testId="FloorMapping"
          title={<div className={styles.parentTitle}>{label}</div>}
          collapsedViewContent={renderCollapseView()}
          defaultExpanded
          className={styles.customToggle}
        >
          <div className={styles.parentDescription}>
            Match {sourceDisplayName} floors to {providerDisplayName} floors for
            credential provisioning.
          </div>
          {vtsFloorList?.map((item: MappedFloor, index: number) => {
            return (
              <div className={styles.flexItem} key={item.value}>
                <div className={styles.mappedContent}>
                  <div className={styles.dropdownLabel}>
                    Map this {sourceDisplayName.toLowerCase()} floor...
                  </div>
                  <Input
                    disabled
                    value={item?.label}
                    onChange={() => null}
                    showClear={false}
                  />
                </div>
                <img
                  src={rightArrow as string}
                  alt="rightArrow"
                  className={styles.rightArrow}
                />
                <div className={styles.mappedContent}>
                  <div className={styles.dropdownLabel}>
                    with this {providerDisplayName} floor
                  </div>
                  <Dropdown
                    items={acsFloorList}
                    value={item?.providerFloorValue || ''}
                    onChange={value => handleChange(value, index)}
                    isFullWidth
                    placeholder={targetFloorPlaceholder}
                    testId={`${targetFloorTestIdPrefix}-${index}`}
                  />
                </div>
              </div>
            );
          })}
        </ToggleView>
      </div>
    </div>
  );
};